import { useEffect, useState } from 'react'
import { inviteLink, inviteMessage } from '@aurora/core'
import { RolePicker } from './RolePicker'
import { useDurableSignOut } from '@platform/auth/web'
import { api } from '@aurora/convex/convex/_generated/api'
import { authClient, clearToken, getToken } from '../../sync/ConvexSync'
import { authReactClient, durableAuth, localAuth } from '../../sync/auth'
import { useAuroraStore } from '../../store/auroraStore'
import { activeChild } from '../../store/selectors'
import { pushToast } from '../../lib/toast'

/**
 * The parent's own account, on the child's Settings tab: who you are in the
 * app, the family and its code, a new password or email, and the way out.
 * Everything here talks to authApi directly — none of it is child data, so
 * none of it lives in the synced store.
 *
 * Two session kinds pass through during the auth transition. A legacy token
 * session calls the token mutations through a short-lived client; a Convex
 * Auth session calls through the authed react client (an empty token means
 * "resolve me from the identity"), and password and email changes go through
 * the authed actions, because the server verifies them against Scrypt.
 */

type Family = {
  householdName?: string
  joinCode?: string
  parents: { name?: string; label?: string; email?: string }[]
  me: { name?: string; label?: string; email?: string } | null
}

export function AccountPanel() {
  const token = getToken() ?? ''
  const legacy = !!token
  const [family, setFamily] = useState<Family | null | undefined>(undefined)
  const [panel, setPanel] = useState<'none' | 'profile' | 'password' | 'email'>('none')
  const [notice, setNotice] = useState<string | null>(null)
  const childName = useAuroraStore((s: any) => (activeChild(s) as any)?.name as string | undefined)

  // The share sheet where there is one (a phone's browser), the clipboard otherwise.
  const invite = async (joinCode: string) => {
    const text = inviteMessage(joinCode, childName)
    try {
      if (navigator.share) await navigator.share({ text })
      else {
        await navigator.clipboard.writeText(text)
        pushToast('Invite copied. Paste it into a message')
      }
    } catch {
      /* the share sheet was closed; nothing to say */
    }
  }

  const load = () => {
    if (!legacy && authReactClient) {
      authReactClient
        .query(api.authApi.family, { token: '' })
        .then((f) => setFamily(f as Family))
        .catch(() => setFamily(null))
      return
    }
    const c = authClient()
    c.query(api.authApi.family, { token })
      .then((f) => setFamily(f as Family))
      .catch(() => setFamily(null))
      .finally(() => c.close())
  }
  useEffect(load, [token])

  const durableSignOut = useDurableSignOut({
    keys: localAuth.keys,
    purge: durableAuth.purgeDurableAuthValues,
  })

  const signOut = async () => {
    if (!legacy) {
      try {
        await durableSignOut()
      } finally {
        window.location.href = '/'
      }
      return
    }
    const c = authClient()
    try {
      await c.mutation(api.authApi.signOut, { token })
    } catch {
      /* the token is going away either way */
    } finally {
      c.close()
    }
    clearToken()
    window.location.href = '/'
  }

  const deleteAccount = async () => {
    const alone = (family?.parents.length ?? 0) <= 1
    const warning = alone
      ? 'Delete your account and the whole family record: every child, note, photo, plan and activity? This cannot be undone.'
      : 'Delete your account? You will be signed out everywhere; the family record stays with the others in the family.'
    if (!window.confirm(warning)) return
    const c = legacy || !authReactClient ? authClient() : null
    try {
      await (c ?? authReactClient!).mutation(api.authApi.deleteAccount, { token })
    } catch (e: any) {
      pushToast(`Could not delete the account: ${String(e?.message ?? e).replace(/^.*Error: /, '')}`)
      return
    } finally {
      c?.close()
    }
    await signOut()
  }

  const done = (msg: string) => {
    setNotice(msg)
    setPanel('none')
    load()
  }

  return (
    <>
      <section className="section">
        <div className="section-head">
          <h3 className="section-title">Your account</h3>
        </div>
        {family === undefined ? (
          <p className="muted">Loading…</p>
        ) : !family?.me ? (
          <p className="muted">This is a demo session, with no account behind it.</p>
        ) : (
          <>
            <dl className="factlist">
              <div className="fact">
                <dt className="fact-label">Name</dt>
                <dd className="fact-value">
                  {family.me.name}
                  {family.me.label ? <span className="muted"> · {family.me.label}</span> : null}
                </dd>
              </div>
              <div className="fact">
                <dt className="fact-label">Email</dt>
                <dd className="fact-value">{family.me.email}</dd>
              </div>
            </dl>
            <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--s-2)' }}>
              <button className="btn btn-quiet btn-sm" onClick={() => setPanel(panel === 'profile' ? 'none' : 'profile')}>
                Change name
              </button>
              <button className="btn btn-quiet btn-sm" onClick={() => setPanel(panel === 'email' ? 'none' : 'email')}>
                Change email
              </button>
              <button className="btn btn-quiet btn-sm" onClick={() => setPanel(panel === 'password' ? 'none' : 'password')}>
                Change password
              </button>
            </div>
            {panel === 'profile' && (
              <ProfileForm me={family.me} token={token} legacy={legacy} onDone={() => done('Saved.')} />
            )}
            {panel === 'email' && (
              <EmailForm token={token} legacy={legacy} onDone={(e) => done(`Email is now ${e}.`)} />
            )}
            {panel === 'password' && (
              <PasswordForm token={token} legacy={legacy} onDone={() => done('Password changed. Other devices were signed out.')} />
            )}
            {notice && <p className="muted" style={{ marginTop: 'var(--s-3)' }}>{notice}</p>}
          </>
        )}
      </section>

      {family?.me && (
        <section className="section">
          <div className="section-head">
            <h3 className="section-title">Your family</h3>
            <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>Everyone who helps raise her</span>
          </div>
          <dl className="factlist">
            {family.householdName && (
              <div className="fact">
                <dt className="fact-label">Household</dt>
                <dd className="fact-value">{family.householdName}</dd>
              </div>
            )}
            {family.parents.map((p) => (
              <div className="fact" key={p.email}>
                <dt className="fact-label">{p.label ?? 'Parent'}</dt>
                <dd className="fact-value">
                  {p.name} <span className="muted">· {p.email}</span>
                </dd>
              </div>
            ))}
            {family.joinCode && (
              <div className="fact">
                <dt className="fact-label">Family code</dt>
                <dd className="fact-value tnum" style={{ letterSpacing: '0.12em', fontWeight: 600 }}>
                  {family.joinCode}
                </dd>
              </div>
            )}
          </dl>
          {family.joinCode && (
            <div className="invite">
              <input className="field invite-link" readOnly value={inviteLink(family.joinCode)}
                aria-label="Invite link" onFocus={(e) => e.target.select()} />
              <button className="btn btn-primary btn-sm" onClick={() => void invite(family.joinCode!)}>
                Invite someone
              </button>
            </div>
          )}
          <p className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 'var(--s-2)' }}>
            Send the link to anyone who helps raise her: the other parent, a grandparent, a nanny, a teacher. It opens Sapling with the family code filled in. The code alone works too: on the web or the phone, choose “Join with a code”. Everything they record shows up with their name on it.
          </p>
        </section>
      )}

      <section className="section">
        <button className="btn btn-ghost" onClick={signOut}>Sign out</button>
        {family?.me && (
          <button className="btn btn-ghost" style={{ marginLeft: 'var(--s-2)' }} onClick={() => void deleteAccount()}>Delete account</button>
        )}
      </section>
    </>
  )
}

function useSubmit(onDone: (v: any) => void) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const submit = async (fn: () => Promise<any>) => {
    setBusy(true)
    setError(null)
    try {
      onDone(await fn())
    } catch (e: any) {
      setError(String(e?.message ?? e).replace(/^.*Error: /, ''))
    } finally {
      setBusy(false)
    }
  }
  return { busy, error, submit }
}

function ProfileForm({ me, token, legacy, onDone }: { me: NonNullable<Family['me']>; token: string; legacy: boolean; onDone: () => void }) {
  const [name, setName] = useState(me.name ?? '')
  const [label, setLabel] = useState(me.label ?? '')
  const { busy, error, submit } = useSubmit(onDone)
  return (
    <form className="auth-fields" style={{ marginTop: 'var(--s-3)' }} onSubmit={(e) => { e.preventDefault(); void submit(async () => {
      if (!legacy) return await authReactClient.mutation(api.authApi.updateProfile, { token: '', name, label })
      const c = authClient()
      try { return await c.mutation(api.authApi.updateProfile, { token, name, label }) } finally { c.close() }
    }) }}>
      <input className="field" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
      <RolePicker value={label} onChange={setLabel} />
      {error && <p className="welcome-error">{error}</p>}
      <button className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
    </form>
  )
}

function EmailForm({ token, legacy, onDone }: { token: string; legacy: boolean; onDone: (email: string) => void }) {
  const [newEmail, setNewEmail] = useState('')
  const [password, setPassword] = useState('')
  const { busy, error, submit } = useSubmit((r) => onDone(r.email))
  return (
    <form className="auth-fields" style={{ marginTop: 'var(--s-3)' }} onSubmit={(e) => { e.preventDefault(); void submit(async () => {
      // A Convex Auth session verifies against the Scrypt secret action-side.
      if (!legacy) return await authReactClient.action(api.authApi.changeEmailAuthed, { newEmail, password })
      const c = authClient()
      try { return await c.mutation(api.authApi.changeEmail, { token, newEmail, password }) } finally { c.close() }
    }) }}>
      <input className="field" type="email" placeholder="New email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} autoComplete="email" />
      <input className="field" type="password" placeholder="Current password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
      {error && <p className="welcome-error">{error}</p>}
      <button className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Saving…' : 'Change email'}</button>
    </form>
  )
}

function PasswordForm({ token, legacy, onDone }: { token: string; legacy: boolean; onDone: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const { busy, error, submit } = useSubmit(onDone)
  return (
    <form className="auth-fields" style={{ marginTop: 'var(--s-3)' }} onSubmit={(e) => { e.preventDefault(); void submit(async () => {
      // A Convex Auth session verifies against the Scrypt secret action-side.
      if (!legacy) return await authReactClient.action(api.authApi.changePasswordAuthed, { current, next })
      const c = authClient()
      try { return await c.mutation(api.authApi.changePassword, { token, current, next }) } finally { c.close() }
    }) }}>
      <input className="field" type="password" placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
      <input className="field" type="password" placeholder="New password (8+ characters)" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
      {error && <p className="welcome-error">{error}</p>}
      <button className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Saving…' : 'Change password'}</button>
    </form>
  )
}
